import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  MailCheck, ShieldCheck, RefreshCw, Trash2, AlertTriangle, Webhook, Ban, Inbox,
} from "lucide-react";
import { PageShell, Stat } from "./PageShell";

const AUTH_TONE = {
  present: { variant: "success", label: "Present" },
  verified: { variant: "success", label: "Verified" },
  pending: { variant: "warning", label: "Pending" },
  missing: { variant: "destructive", label: "Missing" },
  failed: { variant: "destructive", label: "Failed" },
  unknown: { variant: "outline", label: "Unknown" },
};

function AuthRow({ label, data, hint }) {
  const tone = AUTH_TONE[data?.status] || AUTH_TONE.unknown;
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-0" data-testid={`auth-row-${label.toLowerCase()}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold">{label}</span>
          <Badge variant={tone.variant} className="text-[9px] uppercase" data-testid={`auth-status-${label.toLowerCase()}`}>{tone.label}</Badge>
          {data?.policy && <span className="text-[11px] text-muted-foreground font-mono">p={data.policy}</span>}
        </div>
        {data?.detail && <div className="text-[11px] text-muted-foreground mt-0.5 truncate max-w-md">{data.detail}</div>}
        {hint && <div className="text-[11px] text-amber-400 mt-0.5 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {hint}</div>}
      </div>
    </div>
  );
}

export default function PlatformDeliverability() {
  const [snap, setSnap] = useState(null);
  const [feed, setFeed] = useState({ events: [], suppressions: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setBusy(true); setError(false);
    Promise.all([api.get("/platform/email/deliverability"), api.get("/platform/email/events")])
      .then(([d, e]) => { setSnap(d.data); setFeed(e.data); })
      .catch((err) => { setError(true); toast.error(apiErr(err)); })
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const reverify = () => {
    api.post("/platform/email/verify-domain")
      .then((r) => { toast.success(r.data.detail || "Re-verification requested"); load(); })
      .catch((err) => toast.error(apiErr(err)));
  };

  const unsuppress = (email) => {
    api.post(`/platform/email/suppressions/${encodeURIComponent(email)}/remove`)
      .then(() => { toast.success(`Removed ${email} from suppression`); load(); })
      .catch((err) => toast.error(apiErr(err)));
  };

  if (!snap) {
    return (
      <PageShell title="Email Deliverability" testid="platform-deliverability-page">
        {error ? (
          <div className="card-flat p-6 text-center" data-testid="deliverability-error">
            <AlertTriangle className="h-6 w-6 text-amber-400 mx-auto mb-2" />
            <div className="text-sm text-muted-foreground mb-3">Couldn't load domain status.</div>
            <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="deliverability-retry-btn">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Retry
            </Button>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Loading domain status…</div>
        )}
      </PageShell>
    );
  }

  const dmarcWeak = snap.dmarc?.status === "present" && snap.dmarc?.policy === "none";

  return (
    <PageShell
      title="Email Deliverability" testid="platform-deliverability-page"
      subtitle={`Domain authentication and bounce/complaint monitoring for onboarding email sent from ${snap.sender || "the sender domain"}. Keeps transactional mail out of spam at scale.`}
      actions={<Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="deliverability-refresh-btn"><RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh</Button>}
    >
      {dmarcWeak && (
        <div className="card-flat p-4 border-l-2 border-l-amber-400 bg-amber-400/5" data-testid="dmarc-nudge">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">Strengthen DMARC enforcement</div>
              <p className="text-[12px] text-muted-foreground mt-1 max-w-2xl">
                Your DMARC policy is <span className="font-mono">p=none</span> (monitor only) — it reports spoofing but doesn't stop it. Once you've confirmed SPF &amp; DKIM are aligned for <span className="font-mono">{snap.domain}</span> (they show Present below), move DMARC to <span className="font-mono">p=quarantine</span>, then <span className="font-mono">p=reject</span>, to protect your onboarding email from spoofing and keep it out of spam.
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">
                Update the <span className="font-mono">_dmarc.{snap.domain}</span> TXT record at your DNS host, then click <span className="text-foreground">Re-check with Resend</span> below.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={MailCheck} label="Sender domain" value={snap.domain || "—"} sub={snap.provider ? `via ${snap.provider}` : ""} tone="accent" />
        <Stat icon={ShieldCheck} label="Provider status" value={(snap.provider_status || "unknown").replace(/_/g, " ")} sub="Resend domain state" />
        <Stat icon={Ban} label="Suppressed" value={snap.suppressed_count ?? 0} sub="addresses blocked from sending" tone={snap.suppressed_count ? "amber" : undefined} />
        <Stat icon={Webhook} label="Webhook secret" value={snap.webhook_secret_configured ? "Configured" : "Not set"} sub={snap.webhook_secret_configured ? "signatures verified" : "events accepted unverified"} tone={snap.webhook_secret_configured ? "accent" : "amber"} />
      </div>

      {/* Domain auth */}
      <div className="card-flat p-4" data-testid="domain-auth-card">
        <div className="flex items-center justify-between mb-2">
          <div className="label-eyebrow flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-accent" /> Domain authentication (SPF · DKIM · DMARC)</div>
          <Button variant="outline" size="sm" onClick={reverify} data-testid="reverify-domain-btn"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Re-check with Resend</Button>
        </div>
        <AuthRow label="SPF" data={snap.spf} />
        <AuthRow label="DKIM" data={snap.dkim} />
        <AuthRow label="DMARC" data={snap.dmarc} hint={dmarcWeak ? "Policy is p=none (monitor only). Consider p=quarantine or p=reject once aligned." : null} />
        <div className="text-[11px] text-muted-foreground mt-2">
          Checked {new Date(snap.checked_at).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}. Add the webhook endpoint <span className="font-mono">/api/webhooks/resend</span> in your Resend dashboard and set <span className="font-mono">RESEND_WEBHOOK_SECRET</span> to verify signatures.
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Suppressions */}
        <div className="card-flat p-4" data-testid="suppressions-card">
          <div className="label-eyebrow mb-3 flex items-center gap-2"><Ban className="h-3.5 w-3.5 text-red-400" /> Suppression list ({feed.suppressions.length})</div>
          {feed.suppressions.length === 0 ? (
            <div className="text-sm text-muted-foreground">No suppressed addresses. Bounced or complained recipients appear here automatically.</div>
          ) : (
            <div className="space-y-1.5">
              {feed.suppressions.map((s) => (
                <div key={s.email} className="flex items-center justify-between gap-3 text-xs" data-testid={`suppression-${s.email}`}>
                  <div className="min-w-0">
                    <span className="font-mono truncate">{s.email}</span>
                    <Badge variant="outline" className="ml-2 text-[9px] uppercase">{(s.reason || "").replace("email.", "")}{s.bounce_type ? ` · ${s.bounce_type}` : ""}</Badge>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-red-400 hover:text-red-300" onClick={() => unsuppress(s.email)} data-testid={`unsuppress-${s.email}-btn`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent bounce/complaint events */}
        <div className="card-flat p-4" data-testid="email-events-card">
          <div className="label-eyebrow mb-3 flex items-center gap-2"><Inbox className="h-3.5 w-3.5 text-amber-400" /> Recent bounces & complaints ({feed.events.length})</div>
          {feed.events.length === 0 ? (
            <div className="text-sm text-muted-foreground">No bounce or complaint events received yet.</div>
          ) : (
            <div className="space-y-1.5">
              {feed.events.map((e, i) => (
                <div key={i} className="flex items-center gap-3 text-xs" data-testid={`email-event-${i}`}>
                  <span className="font-mono text-muted-foreground shrink-0">{new Date(e.created_at).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  <Badge variant={e.type === "email.complained" ? "destructive" : "warning"} className="text-[9px] uppercase shrink-0">{e.type.replace("email.", "")}</Badge>
                  <span className="truncate">{(e.to || []).join(", ")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
